import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { BUILD_COMMIT } from "@/lib/buildInfo";

/**
 * Sentry in the browser: what gets reported, what deliberately does not, and how
 * a person turns "it broke" into something a developer can read.
 *
 * Three entry points feed it:
 *
 *  - `initSentry()` from `main.tsx`, once, before React mounts.
 *  - The `ErrorBoundary`, which reports the render crash it caught and then
 *    offers a form so the person can say what they were doing. That pairing is
 *    the point — a stack trace says where, only they can say what.
 *  - `openFeedbackForm()`, wired to a "Report a problem" control in the sidebar,
 *    for the failures no exception describes: a wrong number on a report card,
 *    a button that does nothing.
 *
 * Everything is inert without `VITE_SENTRY_DSN`. `resolveSentryOptions` returns
 * null, `initSentry` returns false, and every other function here is a no-op —
 * which is the state of a local checkout and of the test suite.
 */

/** The `import.meta.env` keys this module reads. Passed in, so it can be tested. */
export interface BrowserSentryEnv {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_SENTRY_REPLAY_SAMPLE_RATE?: string;
  VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE?: string;
}

export interface BrowserSentryOptions {
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  sendDefaultPii: false;
}

export const DEFAULT_TRACES_SAMPLE_RATE = 0.1;

/**
 * Both replay rates default to 0 — code that is handed no configuration records
 * nothing — and the committed env files then turn on exactly one of them.
 *
 * Replay records the screen. On this app the screen is a gradebook: names,
 * grades, health notes, a parent's contact details. So the *error* rate is 1 and
 * the *session* rate stays 0, and the difference between those two is the whole
 * decision: error replay holds a rolling buffer in memory and uploads it only
 * once something has already been captured as an error, so an untroubled session
 * is never recorded at all, while session replay would film every afternoon
 * anyone spends in the app.
 *
 * Three things narrow what the kept recording contains, and they are not
 * interchangeable:
 *
 *  - the masking options on `replayIntegration` below, which cover what rrweb
 *    reads out of the DOM — inputs masked and media blocked, text recorded;
 *  - `scrubReplayFrame`, which covers the frames the integration writes itself
 *    — the URL of every fetch and navigation — because masking does not;
 *  - the event processor in `initSentry`, which covers the replay event's own
 *    `urls`, because `beforeSend` is never called for it.
 *
 * Raising VITE_SENTRY_REPLAY_SAMPLE_RATE above 0 is a different decision from
 * the one made here. Read this comment again before doing it.
 */
const DEFAULT_REPLAY_SAMPLE_RATE = 0;

function trimmed(value: string | undefined): string | undefined {
  const result = value?.trim();
  return result ? result : undefined;
}

/** A misconfigured sample rate must not stop the app rendering. Warn, use the default. */
function parseSampleRate(
  raw: string | undefined,
  fallback: number,
  name: string
): number {
  const value = trimmed(raw);
  if (value === undefined) return fallback;

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    console.warn(
      `Sentry: ignoring ${name}=${JSON.stringify(raw)} — expected a number between 0 and 1. Using ${fallback}.`
    );
    return fallback;
  }

  return parsed;
}

/**
 * Init options, or `null` when no DSN is configured.
 *
 * `release` defaults to the commit sha this bundle was built from — the same
 * value `BuildInfo` shows in the sidebar, and the same one the deploy workflow
 * hands the Worker as SENTRY_RELEASE. One release name across both halves is
 * what lets a browser error and the API error behind it be read together.
 */
export function resolveSentryOptions(
  env: BrowserSentryEnv | undefined | null,
  // `null`, not `undefined`, models "there is no release" — `undefined` selects
  // the default, the same trap `BuildInfo`'s props document.
  release: string | null | undefined = BUILD_COMMIT
): BrowserSentryOptions | null {
  const dsn = trimmed(env?.VITE_SENTRY_DSN);
  if (!dsn) return null;

  return {
    dsn,
    environment: trimmed(env?.VITE_SENTRY_ENVIRONMENT) ?? "development",
    // "unknown" is what buildInfo reports for a checkout with no git history;
    // it is not a release, and tagging every event with it would be worse than
    // tagging none.
    release: release && release !== "unknown" ? release : undefined,
    tracesSampleRate: parseSampleRate(
      env?.VITE_SENTRY_TRACES_SAMPLE_RATE,
      DEFAULT_TRACES_SAMPLE_RATE,
      "VITE_SENTRY_TRACES_SAMPLE_RATE"
    ),
    replaysSessionSampleRate: parseSampleRate(
      env?.VITE_SENTRY_REPLAY_SAMPLE_RATE,
      DEFAULT_REPLAY_SAMPLE_RATE,
      "VITE_SENTRY_REPLAY_SAMPLE_RATE"
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      env?.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE,
      DEFAULT_REPLAY_SAMPLE_RATE,
      "VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE"
    ),
    sendDefaultPii: false,
  };
}

/**
 * `/students?search=Jane%20Doe` is a name in a URL, and it reaches Sentry three
 * ways: as the page an event happened on, as a navigation breadcrumb, and as the
 * `fetch` breadcrumb for the API call behind it. Strip the query string from all
 * of them; the path is what identifies the screen.
 *
 * Exported for the test, and because this is the single place that decides it.
 */
export function withoutQueryString(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/**
 * The attribute values `htmlTreeAsString` appends to a DOM selector path.
 *
 * It reads them off the *live* element — `elem.getAttribute("aria-label")` —
 * not off rrweb's serialised copy, so `maskAttributes` (which does mask
 * `aria-label`, `title` and `placeholder` inside the recording's own DOM
 * snapshot) never reaches them. On this app that attribute is exactly where the
 * names are: `${row.student.name} points`, `Note for ${studentName} on
 * ${assignmentName}`.
 *
 * Keep the attribute name, which is what tells one control from another, and
 * drop the value.
 */
const DOM_SELECTOR_ATTRIBUTE = /\[(aria-label|title|alt|name)="[^"]*"\]/g;

/**
 * A `ui.*` breadcrumb's message is a DOM selector path, not a URL:
 * `body > div#root > button.save[aria-label="Save Jane Doe"]`.
 *
 * Two things follow. `withoutQueryString` must not be let near it — it cuts at
 * the first `?` *or* `#`, and `#` is an id selector, so every path that passes
 * through an element with an id would be truncated to `body > div`, which names
 * nothing. And the attribute values in it have to go, because they are what
 * carries a name.
 */
function scrubDomSelector(selector: string): string {
  return selector.replace(DOM_SELECTOR_ATTRIBUTE, "[$1]");
}

/**
 * `ui.click`, `ui.input`, `ui.keyDown`, `ui.slowClickDetected`, … — the
 * breadcrumbs whose message is a selector rather than a URL.
 *
 * Replay makes several of these itself, from its own click/keypress handler
 * rather than `addBreadcrumb`, so `beforeBreadcrumb` never sees them and
 * `scrubReplayFrame` is the only place they can be scrubbed.
 */
function isDomBreadcrumb(breadcrumb: Sentry.Breadcrumb): boolean {
  return breadcrumb.category?.startsWith("ui.") ?? false;
}

function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (typeof breadcrumb.message === "string") {
    breadcrumb = {
      ...breadcrumb,
      message: isDomBreadcrumb(breadcrumb)
        ? scrubDomSelector(breadcrumb.message)
        : withoutQueryString(breadcrumb.message),
    };
  }

  const data = breadcrumb.data;
  if (!data) return breadcrumb;

  const scrubbed: Record<string, unknown> = { ...data };
  // `url` on a fetch/xhr crumb; `from`/`to` on a navigation crumb.
  for (const key of ["url", "from", "to"]) {
    const value = scrubbed[key];
    if (typeof value === "string") scrubbed[key] = withoutQueryString(value);
  }

  return { ...breadcrumb, data: scrubbed };
}

/** Span attributes that are a query string, or a URL that still contains one. */
const QUERY_ATTRIBUTE_KEYS = [
  "http.query",
  "http.fragment",
  "url.query",
  "url.fragment",
];
const URL_ATTRIBUTE_KEYS = ["url", "http.url", "url.full"];

function scrubSpanData<T extends Record<string, unknown>>(data: T): T {
  const scrubbed: Record<string, unknown> = { ...data };
  for (const key of QUERY_ATTRIBUTE_KEYS) delete scrubbed[key];
  for (const key of URL_ATTRIBUTE_KEYS) {
    const value = scrubbed[key];
    if (typeof value === "string") scrubbed[key] = withoutQueryString(value);
  }
  return scrubbed as T;
}

/** The last thing an event passes through before it leaves the browser. */
export function scrubEvent<T extends Sentry.Event>(event: T): T {
  const scrubbed: T = { ...event };

  if (scrubbed.request) {
    const request = { ...scrubbed.request };
    if (request.url) request.url = withoutQueryString(request.url);
    // The SDK splits the query into `query_string` when it parses the URL, and
    // it is present on events that carry no `url` at all — so this is not
    // conditional on there being one.
    delete request.query_string;
    scrubbed.request = request;
  }

  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map(scrubBreadcrumb);
  }

  // `dataCollection.urlQueryParams` does not reach these: the fetch and
  // navigation instrumentation writes the full href into `url.full` and the
  // query into `http.query` on the span itself. `?search=Jane%20Doe` is a name,
  // and a sampled transaction would carry it out of the browser untouched.
  const traceData = scrubbed.contexts?.trace?.data;
  if (traceData) {
    scrubbed.contexts = {
      ...scrubbed.contexts,
      trace: { ...scrubbed.contexts!.trace!, data: scrubSpanData(traceData) },
    };
  }

  if (scrubbed.spans) {
    scrubbed.spans = scrubbed.spans.map((span) =>
      span.data ? { ...span, data: scrubSpanData(span.data) } : span
    );
  }

  // A replay event lists every page its segment covered in `urls`. It is not a
  // field on any other event type, and it reaches Sentry without passing
  // `beforeSend` — see the event processor in `initSentry`, which is what routes
  // replay events through here at all.
  const withUrls = scrubbed as T & { urls?: unknown };
  if (Array.isArray(withUrls.urls)) {
    withUrls.urls = withUrls.urls.map((url) =>
      typeof url === "string" ? withoutQueryString(url) : url
    );
  }

  return scrubbed;
}

/**
 * One custom frame of the replay recording stream.
 *
 * Typed structurally rather than imported: `ReplayFrameEvent` lives in
 * @sentry/replay-internal, which is not a package this app depends on directly.
 * Only the two tags that can carry a URL are described.
 */
export interface ReplayRecordingFrame {
  type: number;
  timestamp: number;
  data: {
    tag: string;
    payload: Record<string, unknown>;
  };
}

/**
 * Strip the query string from a replay recording frame.
 *
 * The recording stream is the other half of the replay leak that `scrubEvent`
 * closes. Sentry's masking options — `maskAllText` and the rest — cover what
 * rrweb captures from the *DOM*, and nothing else; these custom frames are
 * written by the Replay integration itself and never touched by them:
 *
 *  - `performanceSpan`, one per fetch, XHR and navigation, whose `description`
 *    is the raw URL. `/api/students?search=Jane%20Doe` is a name.
 *  - `breadcrumb`. For the ones the core SDK made — console, fetch, navigation —
 *    `beforeBreadcrumb` has already run and this is belt-and-braces. For the
 *    `ui.*` ones it is not: Replay builds those from its own click and keypress
 *    handler, never through `addBreadcrumb`, so this is the *only* hook that
 *    sees them, and their message is a DOM selector path carrying the live
 *    `aria-label` — a student's name, on this app's grids.
 *
 * What this does not reach: `beforeAddRecordingEvent` is only called for custom
 * frames (`isCustomEvent`, type 5). rrweb's own Meta frame, emitted with every
 * full snapshot, carries `window.location.href` complete with its query string,
 * and there is no hook for it. Keep names out of the URL.
 *
 * Returns the frame unchanged when there is nothing to do, and never throws:
 * an exception inside `beforeAddRecordingEvent` makes the SDK drop the frame.
 */
export function scrubReplayFrame(
  frame: ReplayRecordingFrame
): ReplayRecordingFrame {
  const data = frame?.data;
  const payload = data?.payload;
  if (!payload || typeof payload !== "object") return frame;

  if (data.tag === "breadcrumb") {
    const scrubbed = scrubBreadcrumb(
      payload as Sentry.Breadcrumb
    ) as unknown as Record<string, unknown>;
    return { ...frame, data: { ...data, payload: scrubbed } };
  }

  if (data.tag === "performanceSpan") {
    const scrubbed: Record<string, unknown> = { ...payload };

    // The URL, for `resource.fetch`, `resource.xhr` and every `navigation.*`.
    if (typeof scrubbed.description === "string") {
      scrubbed.description = withoutQueryString(scrubbed.description);
    }

    // Request timings and sizes mostly, but the fetch instrumentation puts a
    // `url` in here too, and it is the unabridged one.
    const spanData = scrubbed.data;
    if (spanData && typeof spanData === "object") {
      scrubbed.data = scrubSpanData(spanData as Record<string, unknown>);
    }

    return { ...frame, data: { ...data, payload: scrubbed } };
  }

  return frame;
}

let initialised = false;

/**
 * Start Sentry. Returns whether it actually started, which is false whenever no
 * DSN is configured.
 *
 * Safe to call twice — `main.tsx` calls it once, but React 19's StrictMode and
 * Vite's HMR both re-run modules, and a second `Sentry.init` would replace a
 * live client mid-session.
 */
export function initSentry(
  env: BrowserSentryEnv | undefined | null = import.meta.env
): boolean {
  if (initialised) return true;

  const options = resolveSentryOptions(env);
  if (!options) return false;

  const replayEnabled =
    options.replaysSessionSampleRate > 0 || options.replaysOnErrorSampleRate > 0;

  Sentry.init({
    ...options,
    // Same omissions the API makes, for the same reason: this app renders
    // student records. `userInfo: false` stops the SDK inferring a user from
    // instrumentation — the one that is set comes from `setSentryUser` below and
    // is an id. `httpBodies: []` keeps grade and health payloads out of the
    // breadcrumbs for the XHRs that carry them.
    //
    // Every field has to be stated. Supplying a `dataCollection` object at all
    // switches the SDK's baseline from the restrictive `sendDefaultPii: false`
    // set to its permissive `DEFAULTS` (see `resolveDataCollectionOptions` in
    // @sentry/core), so a key left out here is not "unchanged", it is *opted
    // in* — leaving `urlQueryParams` out resolved it to `true`.
    dataCollection: {
      userInfo: false,
      httpBodies: [],
      // `?search=Jane%20Doe` is a name in a URL.
      urlQueryParams: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      genAI: { inputs: false, outputs: false },
    },
    // The API is same-origin in production (one Worker serves both) and proxied
    // onto this origin in dev, so relative `/api/...` calls are the whole story.
    // Stated rather than left to the default so that adding a second origin is a
    // decision someone makes here.
    tracePropagationTargets: [/^\//],
    integrations: [
      // The router-aware integration, not the bare `browserTracingIntegration`:
      // it names a transaction after the route pattern
      // (`/:schoolSlug/students/:id`) rather than the URL. That is both readable
      // — one transaction per screen instead of one per student — and the reason
      // no student id ends up in a transaction name.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // `autoInject: false`: the widget's own floating button would sit on top
      // of the app's chrome on every page. The sidebar's "Report a problem"
      // opens the same form through `openFeedbackForm()` below.
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: "system",
        showBranding: false,
        showName: true,
        showEmail: true,
        formTitle: "Report a problem",
        submitButtonLabel: "Send report",
        messagePlaceholder:
          "What were you doing, and what did you expect to happen?",
        successMessageText: "Thank you — this has been sent to the developers.",
      }),
      ...(replayEnabled
        ? [
            // See DEFAULT_REPLAY_SAMPLE_RATE above for why this is opt-in at
            // all. Text is recorded: a replay with every string blanked shows
            // no screen, button or message anyone can recognise. What someone
            // typed stays masked and images stay blocked. To hide a region
            // that must never be read, give it `data-sentry-mask` (or
            // `data-sentry-block` for a whole element) — the SDK's defaults.
            Sentry.replayIntegration({
              maskAllText: false,
              maskAllInputs: true,
              blockAllMedia: true,
              // Masking covers what rrweb reads out of the DOM. It does not
              // cover the frames the integration writes itself, which carry the
              // URL of every fetch and navigation — `scrubReplayFrame` does.
              beforeAddRecordingEvent: (event) =>
                scrubReplayFrame(
                  event as unknown as ReplayRecordingFrame
                ) as unknown as typeof event,
            }),
          ]
        : []),
    ],
    beforeSend: scrubEvent,
    // Transactions go out on their own path — `beforeSend` never sees them —
    // and they are where the span attributes carrying the query string live.
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });

  // The replay event — the envelope header listing the segment's `urls`, not the
  // recording — is neither an error nor a transaction, so neither `beforeSend`
  // nor `beforeSendTransaction` is called for it. `prepareReplayEvent` runs the
  // event *processors* and nothing else, which makes this the only hook that
  // sees it. Registered only when replay is on, so the unused case stays inert.
  if (replayEnabled) {
    Sentry.addEventProcessor((event) =>
      event.type === "replay_event" ? scrubEvent(event) : event
    );
  }

  initialised = true;
  return true;
}

/** Whether a client is live. Everything below is a no-op when this is false. */
export function isSentryEnabled(): boolean {
  return Sentry.getClient() !== undefined;
}

export interface SentryUserContext {
  id: string;
  roles: readonly string[];
  schoolId: string;
  /** Whether an admin is currently acting as someone else. */
  impersonating?: boolean;
  /**
   * Whether a platform operator is driving this session through WorkOS support
   * access.
   *
   * Separate from `impersonating`, and both can be set at once: an operator
   * supporting an admin gets the admin's app, "View as" included.
   *
   * A boolean, never the operator's address - `setSentryUser` explains why an
   * email has no business here, and that applies to staff as much as to the
   * schools' own people.
   */
  supportSession?: boolean;
}

/**
 * Say who is using the app, in the same terms the API does: an opaque id, plus
 * role and school as tags.
 *
 * No email and no name, on purpose, and the omission is the same one the API
 * makes (`apps/api/src/observability/sentry.ts`). Everyone here is a member of a
 * school — staff, but also students and their parents — and an error tracker is
 * not somewhere their addresses need to be. The id is enough to tie a report to
 * a session; the feedback form asks for a name and an email separately, where
 * the person supplying them has chosen to.
 */
export function setSentryUser(user: SentryUserContext | null): void {
  if (!isSentryEnabled()) return;

  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag("roles", undefined);
    Sentry.setTag("school_id", undefined);
    Sentry.setTag("impersonating", undefined);
    Sentry.setTag("support_session", undefined);
    return;
  }

  Sentry.setUser({ id: user.id });
  // One tag rather than one per office: Sentry tags are scalars, and "which
  // combination were they holding" is the question worth grouping by.
  Sentry.setTag("roles", [...user.roles].sort().join(","));
  Sentry.setTag("school_id", user.schoolId);
  Sentry.setTag("impersonating", user.impersonating ? true : undefined);
  // `undefined` rather than `false` on an ordinary session, matching the tag
  // above: a tag carried by every event is one nobody can filter by, and the
  // search worth having is "every crash somebody hit while in an account that
  // was not theirs".
  Sentry.setTag("support_session", user.supportSession ? true : undefined);
}

/**
 * Open the feedback form on its own, unattached to any error.
 *
 * Resolves to false when Sentry is off or the modal chunk cannot be loaded — the
 * form is lazy-loaded, so a blocked request or an offline tab is a real outcome
 * and the caller has to be able to say so rather than leave a dead button.
 *
 * The form is built once and kept. `createForm()` returns a *new* dialog every
 * call and `appendToDom()` never removes the last one, so building one per click
 * stacks a dialog into the document on every press of "Report a problem".
 */
let feedbackForm: Awaited<
  ReturnType<NonNullable<ReturnType<typeof Sentry.getFeedback>>["createForm"]>
> | null = null;

export async function openFeedbackForm(): Promise<boolean> {
  const feedback = Sentry.getFeedback();
  if (!feedback) return false;

  try {
    if (!feedbackForm) {
      const form = await feedback.createForm();
      form.appendToDom();
      feedbackForm = form;
    }
    feedbackForm.open();
    return true;
  } catch (error) {
    // Nothing usable is in the DOM if this threw, so don't cache the failure.
    feedbackForm = null;
    console.error("Could not open the feedback form:", error);
    return false;
  }
}

/**
 * Report a React render crash, and get back the event id.
 *
 * The id is what the `ErrorBoundary` then attaches the person's description to,
 * via `sendCrashFeedback`. Without it their account of what happened arrives as
 * a separate, unconnected item.
 */
export function captureRenderError(
  error: unknown,
  componentStack?: string | null
): string | null {
  if (!isSentryEnabled()) return null;

  return Sentry.captureException(error, {
    contexts: componentStack ? { react: { componentStack } } : undefined,
    // Distinguishes a crash the boundary caught from an error reported anywhere
    // else — these are the ones that blanked the page.
    tags: { error_boundary: true },
  });
}

export interface CrashFeedback {
  message: string;
  name?: string;
  email?: string;
  /** The event this is about. Omitted, the feedback stands alone. */
  associatedEventId?: string | null;
}

/**
 * Send what the person wrote about a crash, attached to the crash itself.
 *
 * Returns false when Sentry is off, so the UI can say the report went nowhere
 * instead of thanking them for it.
 */
export function sendCrashFeedback(feedback: CrashFeedback): boolean {
  if (!isSentryEnabled()) return false;

  Sentry.captureFeedback({
    message: feedback.message,
    ...(feedback.name ? { name: feedback.name } : {}),
    ...(feedback.email ? { email: feedback.email } : {}),
    ...(feedback.associatedEventId
      ? { associatedEventId: feedback.associatedEventId }
      : {}),
  });

  return true;
}

/**
 * Note an API failure on the trail leading up to whatever happens next.
 *
 * A breadcrumb rather than an event: a 500 the user recovered from is context,
 * and turning every failed request into its own issue would bury the crashes.
 * `errorId` is the id the API put in its own 500 body — carrying it here is what
 * joins this browser event to the server event that caused it.
 */
export function recordApiFailure(details: {
  method: string;
  url: string;
  status?: number;
  errorId?: string;
}): void {
  if (!isSentryEnabled()) return;

  Sentry.addBreadcrumb({
    category: "api",
    type: "http",
    level: details.status && details.status >= 500 ? "error" : "warning",
    message: `${details.method.toUpperCase()} ${withoutQueryString(details.url)} failed`,
    data: {
      status_code: details.status,
      ...(details.errorId ? { server_error_id: details.errorId } : {}),
    },
  });

  // Deliberately NOT `Sentry.setTag("server_error_id", …)`. That writes to the
  // global scope, which in the browser is the whole session and is never
  // cleared: one 500 at 09:00 would stamp its id onto every unrelated event for
  // the rest of the day, and the join it is supposed to make would point at the
  // wrong server event. The breadcrumb above carries the id, in order, next to
  // the request it belongs to.
}
